import { supabase } from './supabase'

/**
 * Notifications feed — backed by the `notifications` table via the
 * `my_notifications` SECURITY DEFINER RPC (RLS restricts the table to the
 * caller's own rows; the RPC additionally joins the actor's profile).
 *
 * Note the table doubles as the push-delivery log. Rows written BEFORE the
 * `enqueue_notification` migration exist only for users who had an Expo push
 * token — historical feeds are therefore empty for web-only users. From
 * `20260803001000_notifications_migrate_senders.sql` onward every sender writes
 * the row first and pushes second, so new notifications land for everyone.
 */

export type NotificationType =
  | 'match'
  | 'message'
  | 'group_message'
  | 'swipe'
  | 'system'
  | 'tournament_invite'
  | 'tournament_update'
  | 'match_request'

export interface AppNotification {
  id: string
  type: NotificationType
  title: string
  body: string
  data: Record<string, unknown>
  readAt: string | null
  createdAt: string
  /** Profile of whoever triggered this, when the row records one. */
  actor: { gamertag: string; avatar: string | null } | null
}

interface NotificationRow {
  id: string
  type: string
  title: string
  body: string
  data: Record<string, unknown> | null
  read_at: string | null
  created_at: string
  actor_gamertag: string | null
  actor_avatar: string | null
}

function mapRow(r: NotificationRow): AppNotification {
  return {
    id: r.id,
    type: r.type as NotificationType,
    title: r.title,
    body: r.body,
    data: r.data ?? {},
    readAt: r.read_at,
    createdAt: r.created_at,
    actor: r.actor_gamertag
      ? { gamertag: r.actor_gamertag, avatar: r.actor_avatar }
      : null,
  }
}

/** Keyset cursor: the last row of the previous page. */
export interface NotificationCursor {
  createdAt: string
  id: string
}

/**
 * One page of the current user's notifications, newest first.
 *
 * Pagination is a compound (created_at, id) keyset — `created_at` alone is not
 * unique because it defaults to transaction time, so multi-row inserts in one
 * transaction share a timestamp and a bare timestamp cursor skips ties.
 */
export async function getNotifications(
  limit = 20,
  before?: NotificationCursor
): Promise<AppNotification[]> {
  const { data, error } = await supabase.rpc('my_notifications', {
    p_limit: limit,
    p_before: before?.createdAt ?? null,
    p_before_id: before?.id ?? null,
  })
  if (error || !data) {
    if (error) console.error('getNotifications error:', error.message)
    return []
  }
  return (data as NotificationRow[]).map(mapRow)
}

/**
 * A feed entry: either a single notification, or several consecutive ones
 * collapsed into one row.
 *
 * Group chats dominate the raw feed — 86% of rows for a heavy user, with a
 * single chat contributing 278 — so rendering one row per message makes the
 * panel unreadable. Consecutive notifications from the same conversation
 * collapse into one entry showing the newest message and a count.
 */
export interface FeedEntry {
  /** The newest notification in the group; supplies title, body, actor, href. */
  latest: AppNotification
  /** How many notifications this entry represents (1 when not collapsed). */
  count: number
  /** True when any notification in the group is unread. */
  unread: boolean
  /** Every id in the group, so marking read covers all of them. */
  ids: string[]
}

/** Conversation key for collapsing, or null when a row should stand alone. */
function groupKey(n: AppNotification): string | null {
  const d = n.data as { groupChatId?: string; matchId?: string }
  if (n.type === 'group_message' && d.groupChatId) return `g:${d.groupChatId}`
  if (n.type === 'message' && d.matchId) return `m:${d.matchId}`
  // Matches, swipes and system notices are individually meaningful.
  return null
}

/**
 * Collapse runs of consecutive same-conversation notifications.
 *
 * Only *consecutive* rows merge, so an older message from the same chat still
 * appears separately below an unrelated notification — preserving chronology
 * rather than hoisting a stale conversation to the top.
 */
export function groupNotifications(list: AppNotification[]): FeedEntry[] {
  const out: FeedEntry[] = []
  for (const n of list) {
    const key = groupKey(n)
    const prev = out[out.length - 1]
    if (key && prev && groupKey(prev.latest) === key) {
      prev.count += 1
      prev.unread = prev.unread || !n.readAt
      prev.ids.push(n.id)
      continue
    }
    out.push({ latest: n, count: 1, unread: !n.readAt, ids: [n.id] })
  }
  return out
}

/** Unread count for the bell badge. */
export async function getUnreadCount(): Promise<number> {
  const { data, error } = await supabase.rpc('my_unread_notification_count')
  if (error) {
    console.error('getUnreadCount error:', error.message)
    return 0
  }
  return typeof data === 'number' ? data : 0
}

/** Mark specific notifications read, or all of them when `ids` is omitted. */
export async function markRead(ids?: string[]): Promise<void> {
  const { error } = await supabase.rpc('mark_notifications_read', {
    p_ids: ids ?? null,
  })
  if (error) console.error('markRead error:', error.message)
}

/**
 * Where a notification navigates when clicked. Mirrors the native app's
 * `handleNotificationPress` routing so both platforms behave the same.
 * Returns null when there's nothing sensible to open.
 */
export function notificationHref(
  n: AppNotification,
  locale: string
): string | null {
  const d = n.data as {
    matchId?: string
    groupChatId?: string
    slug?: string
    tournamentSlug?: string
  }
  switch (n.type) {
    case 'message':
      return d.matchId ? `/${locale}/app/matches/${d.matchId}` : `/${locale}/app/matches`
    case 'group_message':
      return d.groupChatId
        ? `/${locale}/app/matches/group/${d.groupChatId}`
        : `/${locale}/app/matches`
    case 'match':
      // Older rows predate `matchId` in the payload; fall back to the list.
      return d.matchId ? `/${locale}/app/matches/${d.matchId}` : `/${locale}/app/matches`
    case 'swipe':
      return `/${locale}/app/explore`
    case 'match_request':
      // Identified by design (unlike 'swipe') — go meet the requester.
      return n.actor
        ? `/${locale}/profile/${encodeURIComponent(n.actor.gamertag)}`
        : `/${locale}/app/explore`
    case 'tournament_invite':
    case 'tournament_update': {
      const slug = d.tournamentSlug ?? d.slug
      return slug ? `/${locale}/tournaments/${slug}` : `/${locale}/app/tournaments`
    }
    default:
      return null
  }
}

/** Compact relative time for feed rows: "now", "5m", "3h", "2d", "Aug 3". */
export function timeAgo(iso: string, now: Date = new Date()): string {
  const then = new Date(iso).getTime()
  const secs = Math.max(0, Math.floor((now.getTime() - then) / 1000))
  if (secs < 60) return 'now'
  const mins = Math.floor(secs / 60)
  if (mins < 60) return `${mins}m`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d`
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  })
}
